import {
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  Stack,
  Avatar,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PsychologyAltIcon from "@mui/icons-material/PsychologyAlt";
import MedicationIcon from "@mui/icons-material/Medication";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import NotesIcon from "@mui/icons-material/Notes";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PrintIcon from "@mui/icons-material/Print";
import { headingStyle } from "../../style/LapPageStyle";
import logo from "../../assets/logo_png.png";

/* ─── Shared Styles ─────────────────────────────────── */
const sectionCardStyle = {
  background: "#fff",
  border: "1px solid rgba(0,122,153,0.12)",
  borderRadius: "18px",
  p: 3,
  mb: 3,
  boxShadow: "0 4px 18px rgba(0,122,153,0.08)",
};

const titleStyle = {
  fontWeight: 700,
  fontSize: "15px",
  color: "neutral.teal",
  letterSpacing: 0.5,
};

const subHeadingStyle = {
  fontSize: "13px",
  fontWeight: 700,
  color: "#1F2937",
  mb: 0.5,
  mt: 1.5,
};

const textStyle = {
  color: "#4B5563",
  fontSize: "14px",
  lineHeight: 1.7,
};

/* ─── Helpers ────────────────────────────────────────── */
const SectionHeader = ({ icon, title }) => (
  <Stack direction="row" spacing={1.2} alignItems="center" mb={2}>
    <Avatar
      sx={{
        bgcolor: "rgba(0,151,178,0.12)",
        color: "neutral.teal",
        width: 42,
        height: 42,
      }}
    >
      {icon}
    </Avatar>
    <Typography sx={titleStyle}>{title}</Typography>
  </Stack>
);

const Field = ({ label, value }) => {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <Box mb={1.5}>
      <Typography sx={subHeadingStyle}>{label}</Typography>
      {Array.isArray(value) ? (
        <Stack direction="row" flexWrap="wrap" gap={1} mt={0.5}>
          {value.map((item, i) => (
            <Chip
              key={i}
              label={item}
              size="small"
              sx={{
                bgcolor: "rgba(0,151,178,0.1)",
                color: "neutral.teal",
                fontWeight: 600,
                borderRadius: "8px",
              }}
            />
          ))}
        </Stack>
      ) : (
        <Typography sx={textStyle}>{value}</Typography>
      )}
    </Box>
  );
};

const FieldOrFallback = ({ label, value, fallback = "Not discussed" }) => (
  <Box mb={1.5}>
    <Typography sx={subHeadingStyle}>{label}</Typography>
    {Array.isArray(value) && value.length > 0 ? (
      <Stack direction="row" flexWrap="wrap" gap={1} mt={0.5}>
        {value.map((item, i) => (
          <Chip
            key={i}
            label={item}
            size="small"
            sx={{
              bgcolor: "rgba(0,151,178,0.1)",
              color: "neutral.teal",
              fontWeight: 600,
              borderRadius: "8px",
            }}
          />
        ))}
      </Stack>
    ) : (
      <Typography sx={textStyle}>
        {typeof value === "string" && value ? value : fallback}
      </Typography>
    )}
  </Box>
);

/* ─── Medication Formatter ───────────────────────────── */
const formatMedications = (medications) => {
  if (!Array.isArray(medications)) return medications;
  return medications.map((med) => {
    if (typeof med !== "object" || med === null) return String(med);
    const parts = [med.medication_name];
    if (med.dosage && med.dosage !== "Not documented") parts.push(med.dosage);
    if (med.frequency) parts.push(`(${med.frequency})`);
    return parts.filter(Boolean).join(" ");
  });
};

/* ─── Print Helpers ──────────────────────────────────── */
/**
 * Converts a value (string | string[]) to plain text for the print layout.
 */
const toText = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) return value.length ? value.join(", ") : null;
  return value || null;
};

/**
 * Renders a single labelled row in the print layout.
 */
const PrintRow = ({ label, value, fallback }) => {
  const text = toText(value) ?? fallback;
  if (!text) return null;
  return (
    <tr>
      <td
        style={{
          padding: "5px 12px 5px 0",
          verticalAlign: "top",
          fontWeight: 600,
          color: "#0a4d5c",
          fontSize: "11.5px",
          whiteSpace: "nowrap",
          width: "190px",
        }}
      >
        {label}
      </td>
      <td
        style={{
          padding: "5px 0",
          verticalAlign: "top",
          color: "#374151",
          fontSize: "11.5px",
          lineHeight: "1.65",
        }}
      >
        {text}
      </td>
    </tr>
  );
};

/**
 * Renders a section block in the print layout.
 */
const PrintSection = ({ title, accent, children }) => (
  <div style={{ marginBottom: "18px", pageBreakInside: "avoid" }}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "8px",
      }}
    >
      <div
        style={{
          width: "4px",
          height: "18px",
          borderRadius: "2px",
          background: accent || "#007a99",
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontWeight: 700,
          fontSize: "12px",
          color: accent || "#007a99",
          letterSpacing: "1.2px",
          textTransform: "uppercase",
        }}
      >
        {title}
      </span>
    </div>
    <div
      style={{
        background: "#f8fbfc",
        border: "1px solid #d4eaf0",
        borderRadius: "8px",
        padding: "12px 16px",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>{children}</tbody>
      </table>
    </div>
  </div>
);

/* ─── Print Trigger ──────────────────────────────────── */
/**
 * Opens a new browser window with a fully styled, branded printable version
 * of the SOAP summary. Uses window.print() inside the new window so only
 * the print content is sent to the printer — not the whole app.
 */
const handlePrint = (s, patientName, visitDate, doctor) => {
  const vitals =
    s.objective?.vitals && typeof s.objective.vitals === "object"
      ? Object.entries(s.objective.vitals)
          .map(([k, v]) => `${k}: ${v}`)
          .join("  |  ")
      : s.objective?.vitals || null;

  const physicalExam = Array.isArray(s.objective?.physical_exam)
    ? s.objective.physical_exam.join(", ")
    : s.objective?.physical_exam || null;

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>SOAP Summary — doctat</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display&display=swap" rel="stylesheet" />
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'DM Sans', sans-serif;
      background: #fff;
      color: #1f2937;
      padding: 32px 40px;
      max-width: 800px;
      margin: 0 auto;
    }

    /* ── Header ── */
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding-bottom: 16px;
      border-bottom: 2px solid #007a99;
      margin-bottom: 20px;
    }
    .brand {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .brand-logo {
      height: 38px;
     
    }
    .brand-name {
      font-family: 'DM Serif Display', serif;
      font-size: 22px;
      color: #007a99;
      letter-spacing: -0.3px;
    }
    .brand-tagline {
      font-size: 10px;
      color: #6b7280;
      margin-top: 1px;
      letter-spacing: 0.3px;
    }
    .doc-meta {
      text-align: right;
    }
    .doc-title {
      font-size: 14px;
      font-weight: 700;
      color: #111827;
      letter-spacing: 0.3px;
    }
    .doc-sub {
      font-size: 10.5px;
      color: #6b7280;
      margin-top: 3px;
      line-height: 1.6;
    }

    /* ── Patient Strip ── */
    .patient-strip {
      background: linear-gradient(90deg, #e0f5fa 0%, #f0fbff 100%);
      border: 1px solid #b3e0ed;
      border-radius: 10px;
      padding: 12px 18px;
      margin-bottom: 22px;
      display: flex;
      gap: 32px;
      flex-wrap: wrap;
    }
    .patient-field { display: flex; flex-direction: column; gap: 2px; }
    .patient-label { font-size: 9.5px; font-weight: 700; color: #007a99; text-transform: uppercase; letter-spacing: 0.8px; }
    .patient-value { font-size: 12.5px; font-weight: 600; color: #111827; }

    /* ── Sections ── */
    .section { margin-bottom: 18px; page-break-inside: avoid; }
    .section-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    }
    .section-bar { width: 4px; height: 18px; border-radius: 2px; flex-shrink: 0; }
    .section-title {
      font-weight: 700;
      font-size: 11.5px;
      letter-spacing: 1.2px;
      text-transform: uppercase;
    }
    .section-body {
      background: #f8fbfc;
      border: 1px solid #d4eaf0;
      border-radius: 8px;
      padding: 12px 16px;
    }
    table { width: 100%; border-collapse: collapse; }
    td { vertical-align: top; padding: 4.5px 0; font-size: 11.5px; line-height: 1.65; }
    td.label { font-weight: 600; color: #0a4d5c; white-space: nowrap; width: 190px; padding-right: 12px; }
    td.value { color: #374151; }

    /* ── Footer ── */
    .footer {
      margin-top: 28px;
      padding-top: 12px;
      border-top: 1px solid #e5e7eb;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .footer-brand { font-size: 10px; color: #9ca3af; }
    .footer-brand strong { color: #007a99; }
    .footer-disclaimer { font-size: 9.5px; color: #9ca3af; max-width: 360px; text-align: right; }
    .signature-block { margin-top: 10px; text-align: right; }
    .sig-line { display: inline-block; width: 180px; border-bottom: 1px solid #374151; margin-bottom: 4px; }
    .sig-label { font-size: 10.5px; color: #6b7280; }

    @media print {
      body { padding: 20px 28px; }
      .no-print { display: none !important; }
      @page { margin: 0.5in; size: A4 portrait; }
    }
  </style>
</head>
<body>

  <!-- Header -->
  <div class="header">
    <div class="brand">
      <div class="brand-logo" > 
      <img src=${logo} height=30/></div>
    </div>
    <div class="doc-meta">
      <div class="doc-title">SOAP Summary</div>
      <div class="doc-sub">
        ${doctor || "—"}<br/>
        Generated: ${new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
      </div>
    </div>
  </div>

  <!-- Patient Strip -->
  <div class="patient-strip">
    <div class="patient-field">
      <span class="patient-label">Patient</span>
      <span class="patient-value">${patientName || "—"}</span>
    </div>
    <div class="patient-field">
      <span class="patient-label">Primary Diagnosis</span>
      <span class="patient-value">${toText(s.assessment?.primary_diagnosis) || "—"}</span>
    </div>
    <div class="patient-field">
      <span class="patient-label">Disease Status</span>
      <span class="patient-value">${toText(s.assessment?.disease_status) || "—"}</span>
    </div>
    <div class="patient-field">
      <span class="patient-label">Medications</span>
      <span class="patient-value">${toText(formatMedications(s.subjective?.medications)) || "None"}</span>
    </div>
  </div>

  <!-- S: Subjective -->
  <div class="section">
    <div class="section-header">
      <div class="section-bar" style="background:#007a99;"></div>
      <span class="section-title" style="color:#007a99;">S — Subjective</span>
    </div>
    <div class="section-body">
      <table><tbody>
        ${row("Chief Complaint", s.subjective?.chief_complaint)}
        ${row("History of Present Illness", s.subjective?.history_of_present_illness)}
        ${row("Functional Status", s.subjective?.functional_status)}
        ${row("Symptoms Reported", s.subjective?.symptoms_reported)}
        ${row("Symptom Review", s.subjective?.symptom_review)}
        ${row("Relevant Negatives", s.subjective?.relevant_negatives)}
        ${row("Patient Statements", s.subjective?.patient_statements)}
        ${row("Past Medical History", s.subjective?.past_medical_history, "Not discussed")}
        ${row("Current Medications", formatMedications(s.subjective?.medications))}
        ${row("Allergies", s.subjective?.allergies, "Not discussed")}
        ${row("Family History", s.subjective?.family_history, "Not discussed")}
        ${row("Social History", s.subjective?.social_history, "Not discussed")}
      </tbody></table>
    </div>
  </div>

  <!-- O: Objective -->
  <div class="section">
    <div class="section-header">
      <div class="section-bar" style="background:#0891b2;"></div>
      <span class="section-title" style="color:#0891b2;">O — Objective</span>
    </div>
    <div class="section-body">
      <table><tbody>
        ${row("Vitals", vitals, "Not available")}
        ${row("Physical Exam", physicalExam)}
        ${row("Functional Observations", s.objective?.functional_observations)}
        ${row("Investigations Reviewed", s.objective?.investigations_reviewed)}
        ${row("Test Results", s.objective?.test_results)}
        ${row("Doctor Findings", s.objective?.doctor_findings)}
        ${row("Disease Activity", s.objective?.disease_activity, "Not documented")}
      </tbody></table>
    </div>
  </div>

  <!-- A: Assessment -->
  <div class="section">
    <div class="section-header">
      <div class="section-bar" style="background:#0e7490;"></div>
      <span class="section-title" style="color:#0e7490;">A — Assessment</span>
    </div>
    <div class="section-body">
      <table><tbody>
        ${row("Primary Diagnosis", s.assessment?.primary_diagnosis, "Not yet determined")}
        ${row("Disease Status", s.assessment?.disease_status)}
        ${row("Differential Diagnoses", s.assessment?.differential_diagnoses)}
        ${row("Clinical Impression", s.assessment?.clinical_impression)}
        ${row("Treatment Response", s.assessment?.treatment_response)}
      </tbody></table>
    </div>
  </div>

  <!-- P: Plan -->
  <div class="section">
    <div class="section-header">
      <div class="section-bar" style="background:#155e75;"></div>
      <span class="section-title" style="color:#155e75;">P — Plan</span>
    </div>
    <div class="section-body">
      <table><tbody>
        ${row("Medications Prescribed", s.plan?.medications_prescribed)}
        ${row("Medication Changes", s.plan?.medication_changes)}
        ${row("Tests Ordered", s.plan?.tests_ordered)}
        ${row("Referrals", s.plan?.referrals)}
        ${row("Treatment Plan", s.plan?.treatment_plan)}
        ${row("Lifestyle Recommendations", s.plan?.lifestyle_recommendations)}
        ${row("Follow-up", s.plan?.follow_up, "Not discussed")}
        ${row("Monitoring Plan", s.plan?.monitoring_plan)}
        ${row("Patient Counseling", s.plan?.patient_counseling)}
      </tbody></table>
    </div>
  </div>

  <!-- Signature -->
  <div class="signature-block">
    <div class="sig-line"></div>
    <div class="sig-label">Physician Signature &nbsp;|&nbsp; ${doctor || "Consulting Physician"}</div>
  </div>

  <!-- Footer -->
  <div class="footer">
    <div class="footer-brand">Powered by <strong>doctat</strong> · Clinical Documentation Platform</div>
    <div class="footer-disclaimer">This document is not a legal record and cannot be used for legal purposes. For clinical use only.</div>
  </div>

</body>
</html>`;

  // Helper used inside the template literal above
  function row(label, value, fallback) {
    const text = Array.isArray(value)
      ? value.join(", ")
      : value || fallback || null;
    if (!text) return "";
    return `<tr>
      <td class="label">${label}</td>
      <td class="value">${text}</td>
    </tr>`;
  }

  // const win = window.open("", "_blank", "width=900,height=700");
  // if (win) {
  //   win.document.write(html);
  //   win.document.close();
  // }
  // ── Use a hidden iframe instead of window.open ──
  const existing = document.getElementById("__soap_print_frame__");
  if (existing) existing.remove();

  const iframe = document.createElement("iframe");
  iframe.id = "__soap_print_frame__";
  iframe.style.cssText =
    "position:fixed;top:0;left:0;width:0;height:0;border:none;visibility:hidden;";

  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow.document;

  doc.open();
  doc.write(html);
  doc.close();

  iframe.onload = () => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();

    iframe.contentWindow.onafterprint = () => {
      iframe.remove();
    };
  };
};

/* ─── Main Component ─────────────────────────────────── */
const SOAP = ({ onClose, soapSummary, patientName, visitDate, doctor }) => {
  const s = soapSummary;

  return (
    <Box
      sx={{
        width: "90vw",
        height: "94vh",
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": { display: "none" },
        px: 1,
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box />
        <Typography
          variant="heading1"
          fontWeight={600}
          gutterBottom
          sx={{ ...headingStyle }}
        >
          SOAP Summary
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          {/* ── Print Button ── */}
          {s && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={() => handlePrint(s, patientName, visitDate, doctor)}
              sx={{
                borderColor: "rgba(0,122,153,0.4)",
                color: "neutral.teal",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                px: 1.8,
                py: 0.6,
                "&:hover": {
                  borderColor: "neutral.teal",
                  bgcolor: "rgba(0,151,178,0.06)",
                },
              }}
            >
              Print
            </Button>
          )}

          <IconButton onClick={onClose}>
            <CloseIcon size="small" sx={{ color: "red" }} />
          </IconButton>
        </Stack>
      </Box>

      {s ? (
        <>
          {/* ── SUBJECTIVE ── */}
          <Box sx={sectionCardStyle}>
            <SectionHeader icon={<PsychologyAltIcon />} title="SUBJECTIVE" />
            <Divider sx={{ mb: 2 }} />

            <FieldOrFallback
              label="Chief Complaint"
              value={s.subjective?.chief_complaint}
            />
            <FieldOrFallback
              label="History of Present Illness"
              value={s.subjective?.history_of_present_illness}
            />
            <FieldOrFallback
              label="Functional Status"
              value={s.subjective?.functional_status}
            />
            <Field
              label="Symptoms Reported"
              value={s.subjective?.symptoms_reported}
            />
            <Field
              label="Symptom Review"
              value={s.subjective?.symptom_review}
            />
            <Field
              label="Relevant Negatives"
              value={s.subjective?.relevant_negatives}
            />
            <Field
              label="Patient Statements"
              value={s.subjective?.patient_statements}
            />
            <FieldOrFallback
              label="Past Medical History"
              value={s.subjective?.past_medical_history}
            />
            <Field
              label="Current Medications"
              value={formatMedications(s.subjective?.medications)}
            />
            <FieldOrFallback
              label="Allergies"
              value={s.subjective?.allergies}
            />
            <FieldOrFallback
              label="Family History"
              value={s.subjective?.family_history}
            />
            <FieldOrFallback
              label="Social History"
              value={s.subjective?.social_history}
            />
          </Box>

          {/* ── OBJECTIVE ── */}
          <Box sx={sectionCardStyle}>
            <SectionHeader icon={<MonitorHeartIcon />} title="OBJECTIVE" />
            <Divider sx={{ mb: 2 }} />

            <FieldOrFallback
              label="Vitals"
              value={
                s.objective?.vitals && typeof s.objective.vitals === "object"
                  ? Object.entries(s.objective.vitals)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(" | ")
                  : s.objective?.vitals
              }
              fallback="Not available"
            />
            <Field
              label="Physical Exam"
              value={
                Array.isArray(s.objective?.physical_exam)
                  ? s.objective.physical_exam
                  : s.objective?.physical_exam
                    ? [s.objective.physical_exam]
                    : []
              }
            />
            <Field
              label="Functional Observations"
              value={s.objective?.functional_observations}
            />
            <Field
              label="Investigations Reviewed"
              value={s.objective?.investigations_reviewed}
            />
            <Field label="Test Results" value={s.objective?.test_results} />
            <Field
              label="Doctor Findings"
              value={s.objective?.doctor_findings}
            />
            <FieldOrFallback
              label="Disease Activity"
              value={s.objective?.disease_activity}
              fallback="Not documented"
            />
          </Box>

          {/* ── ASSESSMENT ── */}
          <Box sx={sectionCardStyle}>
            <SectionHeader icon={<NotesIcon />} title="ASSESSMENT" />
            <Divider sx={{ mb: 2 }} />

            <FieldOrFallback
              label="Primary Diagnosis"
              value={s.assessment?.primary_diagnosis}
              fallback="Not yet determined"
            />
            <FieldOrFallback
              label="Disease Status"
              value={s.assessment?.disease_status}
            />
            <Field
              label="Differential Diagnoses"
              value={s.assessment?.differential_diagnoses}
            />
            <FieldOrFallback
              label="Clinical Impression"
              value={s.assessment?.clinical_impression}
            />
            <FieldOrFallback
              label="Treatment Response"
              value={s.assessment?.treatment_response}
            />
          </Box>

          {/* ── PLAN ── */}
          <Box sx={sectionCardStyle}>
            <SectionHeader icon={<MedicationIcon />} title="PLAN" />
            <Divider sx={{ mb: 2 }} />

            <Field
              label="Medications Prescribed"
              value={s.plan?.medications_prescribed}
            />
            <Field
              label="Medication Changes"
              value={s.plan?.medication_changes}
            />
            <Field label="Tests Ordered" value={s.plan?.tests_ordered} />
            <Field label="Referrals" value={s.plan?.referrals} />
            <Field label="Treatment Plan" value={s.plan?.treatment_plan} />
            <Field
              label="Lifestyle Recommendations"
              value={s.plan?.lifestyle_recommendations}
            />
            <FieldOrFallback label="Follow-up" value={s.plan?.follow_up} />
            <Field label="Monitoring Plan" value={s.plan?.monitoring_plan} />
            <Field
              label="Patient Counseling"
              value={s.plan?.patient_counseling}
            />
          </Box>
        </>
      ) : (
        /* Empty state */
        <Box
          sx={{
            height: "60vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Avatar
            sx={{
              width: 70,
              height: 70,
              bgcolor: "rgba(0,151,178,0.12)",
              color: "neutral.teal",
            }}
          >
            <AssignmentIcon sx={{ fontSize: 35 }} />
          </Avatar>
          <Typography
            sx={{ fontSize: "18px", fontWeight: 600, color: "#374151" }}
          >
            No SOAP data available
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SOAP;
